import { createContext, ReactNode, RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { Virtualizer } from '@tanstack/react-virtual'
import { useMediaPlayerContext, useMediaPlayerStateEvent } from '@verbit-ai/verbit-connect-web/player-provider'

import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { useVariableRef } from 'src/hooks/useVariableRef'
import { useTranscriptClient, useTranscriptEvent, useTranscriptUserStateManager } from 'src/contexts/Transcript/TranscriptContext'
import { scrollToBottomEdge, scrollBy } from 'src/utils/scroll'
import { isMobileOperatingSystem } from 'src/utils/plattform'
import { asrTelemetryService } from 'src/services/AsrTelemetryService'
import { FONT_SIZE_BASE } from 'src/models/UIConfiguration'
import { PARAGRAPH_LINE_HEIGHT_PROPORTION, PARAGRAPH_VERTICAL_PADDING } from 'src/components/constants'
import { HOTKEYS } from 'src/utils/hotkeys'
import { isUserSpeaker } from 'src/utils/env'

import { useConfig } from './ConfigurationContext'
import { shouldEnterAutoscrollAfterFontSizeChange, shouldReenterAutoscroll } from './shouldReenterAutoscroll'

type ScrollToParagraphIdParams = { skipIfAlreadyRendered?: boolean } & ({ paragraphId: string } | { offset: number })

export const TranscriptViewerContext = createContext<{
    initManualScrollingSideEffects: () => () => void
    listRef: RefObject<Virtualizer<HTMLDivElement, Element> | null>
    listScrollingContainerRef: RefObject<HTMLDivElement | null>
    autoScrollMode: boolean
    hasNewContent: boolean
    enterAutoScroll: (dto: EnterAutoScrollDto) => void
    exitAutoScroll: () => void
    pauseAutoScroll: () => void
    /**
     * Stops everything that scrolls the transcript on its own: live auto-scroll (latched, as `pauseAutoScroll`) and
     * media playback, which follows the playing paragraph. For anything that brings the user to a quote.
     */
    stopAutoScrolling: () => void
} | null>(null)

const TranscriptViewerAPIContext = createContext<{ scrollToParagraphId: (params: ScrollToParagraphIdParams) => void } | null>(null)

const UP = 38
const DOWN = 40

const AUTO_SCROLL_TRIGGER_THRESHOLD_PX = 10
const AUTO_SCROLL_EXIT_THRESHOLD_LINES = 4
const ARROW_KEY_SCROLL_DISTANCE_PX = 400
const ARROW_KEY_SCROLL_SPEED_PX_PER_SEC = ARROW_KEY_SCROLL_DISTANCE_PX

const VISIBILITY_CHANGE_EVENT: keyof DocumentEventMap = 'visibilitychange'
const POST_INACTIVITY_REFRESH_TIMEOUT_MS = 30 * 60 * 1000

type TranscriptViewerProviderProps = { isInitialFontAvailable: boolean; children: ReactNode }

type EnterAutoScrollDto = { shouldFocusContainer: boolean }

export const TranscriptViewerProvider = (props: TranscriptViewerProviderProps) => {
    const { isInitialFontAvailable, children } = props
    const transcriptClient = useTranscriptClient()
    const userStateManager = useTranscriptUserStateManager()

    const listRef = useRef<Virtualizer<HTMLDivElement, Element>>(null)
    const listScrollingContainerRef = useRef<HTMLDivElement>(null)

    const [autoScrollMode, setAutoScrollMode] = useState(false)
    const autoScrollModeRef = useVariableRef(autoScrollMode)
    const { mediaPlayer } = useMediaPlayerContext()
    const autoScrollPausedByUserRef = useRef(false)
    const wasAutoscrollOnRef = useRef(false)

    useEffect(() => {
        if (autoScrollMode && !wasAutoscrollOnRef.current) {
            mediaPlayer.pause()
        }
        wasAutoscrollOnRef.current = autoScrollMode
    }, [autoScrollMode, mediaPlayer])

    useEffect(() => {
        asrTelemetryService.setAutoScrollMode(autoScrollMode)
    }, [autoScrollMode])

    const [{ fontSizePercentage }] = useConfig()
    const [recentFontSizeChange, setRecentFontSizeChange] = useState(false)

    const [hasNewContent, setHasNewContent] = useState(false)

    const autoScrollExitThresholdPixels = useRef(0)

    const setSmoothScrollEnabled = (isEnabled: boolean) => {
        if (!listScrollingContainerRef.current) return
        listScrollingContainerRef.current.style.scrollBehavior = isEnabled ? 'smooth' : 'auto'
    }

    const enterAutoScroll = useCallback(
        ({ shouldFocusContainer }: EnterAutoScrollDto) => {
            // when initial font is loaded, the list is scrolled to the last item immediately,
            // setting list's `scrollUpdateWasRequested` flag to true and cancelling the autoscroll;
            // sometimes though, due to async issues, when scroll happens between frames, autoscroll isn't cancelled,
            // and after bringing user to the bottom, transcript jumps back up and continues scrolling slowly
            // all the way through; to prevent this, we do not start autoscrolling before initial font is loaded
            if (!isInitialFontAvailable) {
                return
            }

            autoScrollModeRef.current = true
            autoScrollPausedByUserRef.current = false
            setAutoScrollMode(true)
            setHasNewContent(false)
            scrollToBottomEdge(listScrollingContainerRef.current)
            setSmoothScrollEnabled(true)
            // when autoscroll is caused by font size change, scrolling container
            // should not get focus, cause then font control buttons would
            // _lose_ focus, which contradicts VPAT compliance requirements
            if (shouldFocusContainer) {
                listScrollingContainerRef.current?.focus()
            }
        },
        [autoScrollModeRef, isInitialFontAvailable],
    )

    useEffect(() => {
        if (isInitialFontAvailable) {
            enterAutoScroll({ shouldFocusContainer: true })
        }
    }, [enterAutoScroll, isInitialFontAvailable])

    useEffect(() => {
        setRecentFontSizeChange(true)

        const fontSize = Math.round((FONT_SIZE_BASE * fontSizePercentage) / 100)
        const lineHeight = fontSize * PARAGRAPH_LINE_HEIGHT_PROPORTION
        autoScrollExitThresholdPixels.current = AUTO_SCROLL_EXIT_THRESHOLD_LINES * lineHeight + PARAGRAPH_VERTICAL_PADDING * 2

        const listScrollingContainer = listScrollingContainerRef.current
        if (!listScrollingContainer) return
        const { scrollTop, scrollHeight, clientHeight } = listScrollingContainer
        const nearBottom = scrollTop >= scrollHeight - clientHeight - AUTO_SCROLL_TRIGGER_THRESHOLD_PX

        const timer = setTimeout(() => {
            setRecentFontSizeChange(false)
            if (
                shouldEnterAutoscrollAfterFontSizeChange({
                    pauseLatched: autoScrollPausedByUserRef.current,
                    nearBottom,
                })
            ) {
                enterAutoScroll({ shouldFocusContainer: false })
            }
        }, 200)

        return () => clearTimeout(timer)
    }, [enterAutoScroll, fontSizePercentage])

    const exitAutoScroll = useCallback(() => {
        setSmoothScrollEnabled(false)
        autoScrollModeRef.current = false
        setAutoScrollMode(false)
    }, [autoScrollModeRef])

    const pauseAutoScroll = useCallback(() => {
        autoScrollPausedByUserRef.current = true
        exitAutoScroll()
    }, [exitAutoScroll])

    const stopAutoScrolling = useCallback(() => {
        pauseAutoScroll()
        mediaPlayer.pause()
    }, [mediaPlayer, pauseAutoScroll])

    const wasPlayingRef = useRef(false)
    useEffect(() => {
        // A new AudioPlayer does not emit the old one's pause. Seed from the instance we just subscribed to.
        wasPlayingRef.current = mediaPlayer.getLocalState().isMediaPlaying
    }, [mediaPlayer])
    const onMediaPlayerState = useCallback(
        (state: { isMediaPlaying: boolean }) => {
            const playbackStarted = state.isMediaPlaying && !wasPlayingRef.current
            wasPlayingRef.current = state.isMediaPlaying
            if (playbackStarted) {
                pauseAutoScroll()
            }
        },
        [pauseAutoScroll],
    )
    useMediaPlayerStateEvent(onMediaPlayerState)

    // TODO: search not working
    // TODO: handle manual scroll bar thumb scroll
    // TODO: rename
    const initManualScrollingSideEffects = useCallback(() => {
        const listScrollingContainer = listScrollingContainerRef.current
        let lastScrollTop = listScrollingContainer?.scrollTop

        const handleScroll = (isScrollingUp: boolean, isUserScrollDown: boolean) => {
            if (!listScrollingContainer) return

            const { scrollTop, scrollHeight, clientHeight } = listScrollingContainer
            const nearBottom = scrollTop >= scrollHeight - clientHeight - AUTO_SCROLL_TRIGGER_THRESHOLD_PX

            if (isScrollingUp) {
                // only exit autoscroll when at least ${autoScrollExitThreshold}px away from the bottom of the page,
                // in order to avoid accidental exits when ASR chunk is big enough to add an extra line and trigger
                // scroll event (which will be treated as scrolling up)
                if (
                    scrollHeight > clientHeight &&
                    !recentFontSizeChange &&
                    scrollHeight - scrollTop - clientHeight > autoScrollExitThresholdPixels.current
                ) {
                    exitAutoScroll()
                }
            } else if (
                shouldReenterAutoscroll({
                    pauseLatched: autoScrollPausedByUserRef.current,
                    nearBottom,
                    isUserScrollDown,
                })
            ) {
                autoScrollModeRef.current = true
                autoScrollPausedByUserRef.current = false
                setHasNewContent(false)
                setAutoScrollMode(true)
            }
        }

        let isWheelScrollUp = false
        let isWheelScrollDown = false

        const onWheel = (e: WheelEvent) => {
            isWheelScrollUp = e.deltaY < 0
            isWheelScrollDown = e.deltaY > 0
        }

        const onScroll = () => {
            if (lastScrollTop !== undefined && listScrollingContainer) {
                const isScrollingUp = lastScrollTop - listScrollingContainer.scrollTop >= 1

                if (isScrollingUp && isWheelScrollUp) {
                    handleScroll(true, false)
                } else if (!isScrollingUp) {
                    // Only a wheel-down counts as user-down. Browsers can raise scrollTop when ASR
                    // grows the list while pinned at the live edge; treating that as user-down
                    // would undo Pause. Scrollbar-down stays blocked (same as today's scrollbar-up).
                    handleScroll(false, isWheelScrollDown)
                }
            }

            lastScrollTop = listScrollingContainer?.scrollTop
            isWheelScrollUp = false
            isWheelScrollDown = false
        }

        /** keyboard */
        const arrowKeyAutoScrollCancelRef = { current: false }

        const enterArrowKeyAutoScroll = async (isScrollingUp: boolean): Promise<void> => {
            if (autoScrollModeRef.current && !isScrollingUp) return

            const scrollTopDelta = ARROW_KEY_SCROLL_DISTANCE_PX * (isScrollingUp ? -1 : 1)

            return scrollBy(
                scrollTopDelta,
                ARROW_KEY_SCROLL_SPEED_PX_PER_SEC,
                listScrollingContainerRef.current!,
                arrowKeyAutoScrollCancelRef,
            )
                .then(() => handleScroll(isScrollingUp, !isScrollingUp))
                .then(() => enterArrowKeyAutoScroll(isScrollingUp))
                .catch((...args) => {
                    arrowKeyAutoScrollCancelRef.current = false
                    console.warn(...args)
                })
        }

        const quitArrowKeyAutoScroll = () => {
            arrowKeyAutoScrollCancelRef.current = true
        }

        const onKeyDown = (e: KeyboardEvent) => {
            // only do the scrolling while the user is not editing
            if (userStateManager.getLocalState().selection === null) {
                switch (true) {
                    case HOTKEYS.arrowUp.check(e):
                        e.preventDefault()
                        if (!e.repeat) {
                            arrowKeyAutoScrollCancelRef.current = false
                            enterArrowKeyAutoScroll(true)
                        }
                        break
                    case HOTKEYS.arrowDown.check(e):
                        e.preventDefault()
                        if (!e.repeat) {
                            arrowKeyAutoScrollCancelRef.current = false
                            enterArrowKeyAutoScroll(false)
                        }
                        break
                }
            }
        }

        const onKeyUp = ({ keyCode }: KeyboardEvent) => {
            switch (keyCode) {
                case UP:
                case DOWN:
                    quitArrowKeyAutoScroll()
            }
        }

        /** mobile **/
        const onTouchStart = ({ touches: [touchStart] }: TouchEvent) => {
            let prevClientY = touchStart.clientY
            let isScrollingUp = false
            let multiTouchActive = false

            const onTouchMove = (e: TouchEvent) => {
                // A pinch is not vertical scrolling. Measuring touches[0] through one would call
                // handleScroll on every frame and toggle autoscroll while the user is only zooming.
                if (e.touches.length > 1) {
                    multiTouchActive = true

                    return
                }

                const touch = e.touches[0]

                if (!touch) return

                // The finger left behind by a pinch is not necessarily the one we were tracking, so
                // re-seed instead of measuring the first move after it against a stale position.
                if (multiTouchActive) {
                    multiTouchActive = false
                    prevClientY = touch.clientY

                    return
                }

                const deltaY = touch.clientY - prevClientY
                prevClientY = touch.clientY
                isScrollingUp = deltaY > 0

                handleScroll(isScrollingUp, !isScrollingUp)
            }

            // Remove all three explicitly rather than relying on { once: true }: only the listener
            // that actually fires is auto-removed, so the other end-of-gesture one would accumulate.
            const stopTracking = () => {
                listScrollingContainer?.removeEventListener('touchmove', onTouchMove)
                listScrollingContainer?.removeEventListener('touchend', stopTracking)
                listScrollingContainer?.removeEventListener('touchcancel', stopTracking)
            }

            listScrollingContainer?.addEventListener('touchmove', onTouchMove)
            listScrollingContainer?.addEventListener('touchend', stopTracking)
            // the browser fires touchcancel, not touchend, when it takes the gesture over to pinch-zoom
            listScrollingContainer?.addEventListener('touchcancel', stopTracking)
        }

        listScrollingContainer?.addEventListener('keydown', onKeyDown)
        listScrollingContainer?.addEventListener('keyup', onKeyUp)

        // "scroll" event fires for any scrollTop change, not just user actions (e.g. content height
        // changes from ASR corrections, programmatic scrolls). On mobile, we avoid it entirely and use
        // touch events instead. On desktop, both exit-on-scroll-up and pause-latched re-enter require a
        // preceding wheel so auto-pin / scrollbar motion cannot toggle autoscroll. Unlatched re-enter
        // still uses "scroll" when the viewer is already near the bottom.
        const isMobile = isMobileOperatingSystem()
        if (isMobile) {
            listScrollingContainer?.addEventListener('touchstart', onTouchStart)
        } else {
            listScrollingContainer?.addEventListener('wheel', onWheel, { passive: true })
            listScrollingContainer?.addEventListener('scroll', onScroll)
        }

        return () => {
            listScrollingContainer?.removeEventListener('keydown', onKeyDown)
            listScrollingContainer?.removeEventListener('keyup', onKeyUp)
            if (isMobile) {
                listScrollingContainer?.removeEventListener('touchstart', onTouchStart)
            } else {
                listScrollingContainer?.removeEventListener('wheel', onWheel)
                listScrollingContainer?.removeEventListener('scroll', onScroll)
            }
        }
    }, [autoScrollModeRef, exitAutoScroll, recentFontSizeChange, userStateManager])

    useTranscriptEvent('', () => {
        if (autoScrollMode) {
            scrollToBottomEdge(listScrollingContainerRef.current)
        } else {
            setHasNewContent(true)
        }
    })

    const scrollToParagraphId = useCallback(
        (params: ScrollToParagraphIdParams) => {
            let pIndex = -1

            if ('offset' in params) {
                pIndex = transcriptClient.getParagraphIndexByInsightOffset(params.offset)
            } else {
                pIndex = transcriptClient.getParagraphIds().findIndex((id) => id === params.paragraphId)
            }

            if (pIndex >= 0) {
                exitAutoScroll()
                if (listRef.current) {
                    const renderedItems = listRef.current.getVirtualItems()
                    const isAlreadyRendered = renderedItems.findIndex(({ index }) => index === pIndex) !== -1
                    const shouldScroll = params.skipIfAlreadyRendered ? !isAlreadyRendered : true
                    if (shouldScroll) {
                        listRef.current.scrollToIndex(pIndex, { align: 'start' })
                    }
                }
            }
        },
        [exitAutoScroll, transcriptClient],
    )

    const lastVisibleAtRef = useRef<number | null>(null)

    // since we cannot guarantee proper handling of autoscroll while the page isn't focused,
    // we exit autoscroll when page is hidden, and enter it again once the focus is restored;
    // additionally, to prevent processes from going stale in "sleeping" tabs,
    // if the page was invisible for more than 30 mins, we reload the page after it regains focus
    useEffect(() => {
        const visibilityChangeListener = () => {
            asrTelemetryService.setTabVisible(!document.hidden)

            if (document.hidden) {
                lastVisibleAtRef.current = Date.now()
                exitAutoScroll()
            } else {
                if (lastVisibleAtRef.current !== null) {
                    const inactiveDurationMs = Date.now() - lastVisibleAtRef.current
                    console.log(`[visibility] Document visibility restored after ${inactiveDurationMs} ms`)

                    if (inactiveDurationMs >= POST_INACTIVITY_REFRESH_TIMEOUT_MS && !isUserSpeaker()) {
                        window.location.reload()

                        return
                    }
                }

                lastVisibleAtRef.current = null
                enterAutoScroll({ shouldFocusContainer: true })
            }
        }

        document.addEventListener(VISIBILITY_CHANGE_EVENT, visibilityChangeListener)

        return () => {
            document.removeEventListener(VISIBILITY_CHANGE_EVENT, visibilityChangeListener)
        }
    }, [enterAutoScroll, exitAutoScroll])

    return (
        <TranscriptViewerContext.Provider
            value={useShallowMemo({
                initManualScrollingSideEffects,
                listRef,
                listScrollingContainerRef,
                autoScrollMode,
                hasNewContent,
                enterAutoScroll,
                exitAutoScroll,
                pauseAutoScroll,
                stopAutoScrolling,
            })}>
            <TranscriptViewerAPIContext.Provider value={useShallowMemo({ scrollToParagraphId })}>
                {children}
            </TranscriptViewerAPIContext.Provider>
        </TranscriptViewerContext.Provider>
    )
}

export const useTranscriptViewer = () => {
    const value = useContext(TranscriptViewerContext)

    if (!value) {
        throw new Error('You have forgot to use <TranscriptViewerProvider />, shame on you.')
    }

    return value
}

export const useTranscriptViewerAPI = () => {
    const value = useContext(TranscriptViewerAPIContext)

    if (!value) {
        throw new Error('You have forgot to use <TranscriptViewerProvider />, shame on you.')
    }

    return value
}
